import { differenceInSeconds } from 'date-fns';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceCallback } from 'usehooks-ts';
import { z } from 'zod';
import { ConversationDto } from '@/Domain/api/Conversation/dto/Conversation.dto';
import { UpdateVisitorRequest } from '@/Domain/api/Conversation/request/UpdateVisitor.request';
import { AssignUser } from '@/Domain/components/Inbox/ConversationDetails/Tabs/DetailsTab/AssignUser';
import { DetailsItem } from '@/Domain/components/Inbox/ConversationDetails/Tabs/DetailsTab/DetailsItem';
import { EditableItem } from '@/Domain/components/Inbox/ConversationDetails/Tabs/DetailsTab/EditableItem';
import { ConversationOutcome } from '@/Domain/components/Inbox/ConversationOutcome/ConversationOutcome';
import { ConversationSentiment } from '@/Domain/components/Inbox/ConversationSentiment/ConversationSentiment';
import { ConversationTopic } from '@/Domain/components/Inbox/ConversationTopic/ConversationTopic';
import {
  UnifiedSentiment,
  UnifiedOutcome,
  UnifiedTopic,
  UnifiedFlagged,
  UnifiedAlerts,
} from '@/Domain/components/Inbox/UnifiedAnalytics';
import {
  Accordion,
  AccordionItem,
  AccordionContent,
  AccordionTrigger,
} from '@/Domain/components/Shared/Accordion/Accordion';
import { AppHelper } from '@/Domain/helper/App.helper';
import { ConversationHelper } from '@/Domain/helper/Conversation.helper';
import { useObservedMetadataKeys } from '@/Domain/hooks/Action/useObservedMetadataKeys.hook';
import { useUpdateVisitor } from '@/Domain/hooks/Conversation/useUpdateVisitor.hook';
import { safeFormat, toValidDate } from '@/Domain/utils/safeDate';
import { toDisplayValue } from '@/Domain/utils/toDisplayValue';

const stripQueryParams = (url: string): string => {
  try {
    const u = new URL(url);
    return `${u.origin}${u.pathname}`.replace(/\/+$/, '');
  } catch {
    return url.split('?')[0];
  }
};

type Props = {
  conversation: ConversationDto;
};

enum DetailsSectionEnum {
  Conversation = 'Conversation',
  Visitor = 'Visitor',
  Memory = 'Memory',
}

export const DetailsTab = ({ conversation }: Props) => {
  const { t, i18n } = useTranslation('inbox_v2');

  const { handleUpdate } = useUpdateVisitor(conversation.id);

  const { data: observedMetadataKeys } = useObservedMetadataKeys();
  const memoryKeySet = new Set(
    (observedMetadataKeys ?? []).filter((k) => k.is_memory_key).map((k) => k.key),
  );
  const clientMetadataEntries = Object.entries(conversation.client_metadata || {});
  const memoryEntries = clientMetadataEntries.filter(([key]) => memoryKeySet.has(key));
  const visitorMetadataEntries = clientMetadataEntries.filter(([key]) => !memoryKeySet.has(key));

  const handleVisitorUpdate = useDebounceCallback(
    (visitor: Partial<UpdateVisitorRequest>) => handleUpdate(visitor),
    1000,
  );

  const hasUnifiedAnalysis = !!conversation.unified_analysis;
  const hasLegacyExperiments = Object.keys(conversation.single_label_experiments || {}).length > 0;
  const showUnifiedAnalytics = hasUnifiedAnalysis || !hasLegacyExperiments;

  const formatSeconds = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) {
      const m = Math.round(seconds / 60);
      return m >= 60 ? '1h' : `${m}m`;
    }
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds % 3600) / 60);
    if (m >= 60) return `${h + 1}h`;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const formatDuration = (
    start: string | null | undefined,
    end: string | null | undefined,
  ): string => {
    const startDate = toValidDate(start);
    const endDate = toValidDate(end);
    if (!startDate || !endDate) return '-';
    const totalSeconds = Math.max(0, differenceInSeconds(endDate, startDate));
    if (totalSeconds < 60) return '< 1 min';

    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    return `${minutes}m`;
  };

  if (!conversation) {
    return null;
  }

  const renderValueElement = (v: unknown): ReactElement => {
    if (v == null) return <span>-</span>;

    const t = typeof v;
    if (t === 'string' || t === 'number' || t === 'boolean') {
      const text = String(v);
      if (t === 'string' && /^https?:\/\//.test(text)) {
        return (
          <a
            href={text}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="metadata-link"
            className="truncate text-sm text-blue-600 hover:text-blue-800 hover:underline"
            title={text}
          >
            {text}
          </a>
        );
      }
      return <span className="truncate text-sm text-foreground">{text}</span>;
    }

    return (
      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
        {toDisplayValue(v)}
      </pre>
    );
  };

  return (
    <Accordion
      type="multiple"
      defaultValue={[DetailsSectionEnum.Conversation, DetailsSectionEnum.Visitor]}
      className="pb-12"
    >
      <div className="flex flex-col gap-5 rounded-xl bg-accent p-4 text-sm">
        <header className="flex items-center justify-between">
          <p className="font-medium text-foreground">{t('conversation_details.insights')}</p>
        </header>
        {showUnifiedAnalytics ? (
          <>
            <UnifiedSentiment conversation={conversation} />
            <UnifiedOutcome conversation={conversation} />
            <UnifiedTopic conversation={conversation} />
            <UnifiedAlerts conversation={conversation} />
            <UnifiedFlagged conversation={conversation} />
          </>
        ) : (
          <>
            <ConversationSentiment conversation={conversation} />
            <ConversationOutcome conversation={conversation} />
            <ConversationTopic conversation={conversation} />
          </>
        )}
      </div>

      <AccordionItem value={DetailsSectionEnum.Conversation}>
        <AccordionTrigger>{t('conversation_details.conversation')}</AccordionTrigger>
        <AccordionContent>
          <AssignUser
            assignee={conversation.assignee}
            conversationId={conversation.id}
            location="details-tab"
          />
          <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-4">
              <DetailsItem
                label={t('conversation_details.items.channel')}
                value={ConversationHelper.getConversationSourceLabel(conversation.source)}
                tooltipContentProps={{
                  align: 'start',
                }}
              />
              <DetailsItem
                label={t('conversation_details.items.url')}
                tooltipContentProps={{
                  className: '',
                  align: 'start',
                }}
                value={conversation.url || '-'}
                ValueComponent={
                  <p className="truncate text-sm text-foreground">
                    {conversation.url ? stripQueryParams(conversation.url) : '-'}
                  </p>
                }
                tooltipValue={conversation.url || undefined}
              />
              <DetailsItem
                label={t('conversation_details.items.language')}
                value={(() => {
                  const lang = conversation.most_frequent_language;
                  if (!lang) return null;
                  // Try locale code (e.g. en_GB) in languages namespace, then ISO 639-1 (e.g. en) in common namespace
                  if (i18n.exists(`languages:${lang}`)) return t(`languages:${lang}`);
                  if (i18n.exists(`common:languages.${lang}`)) return t(`common:languages.${lang}`);
                  return null;
                })()}
                tooltipContentProps={{
                  align: 'start',
                }}
              />
              <DetailsItem
                label={t('conversation_details.items.started_at')}
                value={safeFormat(conversation.created_at, 'HH:mm, dd MMM yy')}
                tooltipContentProps={{
                  align: 'start',
                }}
              />
              <DetailsItem
                label={t('conversation_details.items.lass_active')}
                value={safeFormat(conversation.last_message_at, 'HH:mm, dd MMM yy')}
                tooltipContentProps={{
                  align: 'start',
                }}
              />
              <DetailsItem
                label={t('conversation_details.items.duration')}
                value={formatDuration(conversation.created_at, conversation.last_message_at)}
                tooltipContentProps={{
                  align: 'start',
                }}
              />
              {conversation.csat_rating != null && (
                <DetailsItem
                  label={t('conversation_details.items.csat_rating')}
                  value={`${conversation.csat_rating} / 5`}
                  tooltipContentProps={{
                    align: 'start',
                  }}
                />
              )}
              {conversation.ttfr_seconds != null && (
                <DetailsItem
                  label={t('conversation_details.items.ttfr')}
                  value={formatSeconds(conversation.ttfr_seconds)}
                  tooltipContentProps={{
                    align: 'start',
                  }}
                />
              )}
            </section>
          </div>
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value={DetailsSectionEnum.Visitor}>
        <AccordionTrigger>{t('conversation_details.visitor')}</AccordionTrigger>
        <AccordionContent>
          <div className="flex flex-col gap-4 pt-1 pr-1">
            <EditableItem
              label={t('conversation_details.items.name')}
              defaultValue={conversation.visitor_name}
              validationSchema={z.string().max(50).optional()}
              onChange={(value) => handleVisitorUpdate({ name: value })}
            />
            <EditableItem
              label={t('conversation_details.items.email')}
              defaultValue={conversation.visitor_email}
              validationSchema={z.string().email().optional()}
              onChange={(value) => handleVisitorUpdate({ email: value })}
            />
            <EditableItem
              label={t('conversation_details.items.phone_number')}
              defaultValue={conversation.visitor_phone_number}
              validationSchema={z.string().max(50).optional()}
              onChange={(value) => handleVisitorUpdate({ phone_number: value })}
            />
            {Object.entries(conversation.data_gathering || {}).map(([key, value]) => (
              <DetailsItem
                key={key}
                label={AppHelper.humanizeSnakeCase(key)}
                value={value}
                tooltipContentProps={{
                  align: 'start',
                }}
              />
            ))}
            {visitorMetadataEntries.map(([key, value]) => (
              <DetailsItem
                key={key}
                label={AppHelper.humanizeSnakeCase(key)}
                value={value}
                ValueComponent={renderValueElement(value)}
                tooltipContentProps={{
                  align: 'start',
                }}
              />
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>

      {memoryEntries.length > 0 && (
        <AccordionItem value={DetailsSectionEnum.Memory}>
          <AccordionTrigger>{t('conversation_details.memory')}</AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-col gap-4 pt-1 pr-1">
              {memoryEntries.map(([key, value]) => (
                <DetailsItem
                  key={key}
                  label={AppHelper.humanizeSnakeCase(key)}
                  value={value}
                  ValueComponent={renderValueElement(value)}
                  tooltipContentProps={{
                    align: 'start',
                  }}
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      )}
    </Accordion>
  );
};
